import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';

// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

// Function to get Yahoo Finance URL for mutual funds
const getYahooFinanceUrl = (symbol) => {
  const yahooUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${symbol}?interval=1d&range=1d`;
  return `/api/yahoo-chart?url=${encodeURIComponent(yahooUrl)}`;
};

const DebtMutualFund = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-debt-mutual-fund', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [fundTransactions, setFundTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editNavValue, setEditNavValue] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editFundSymbol, setEditFundSymbol] = useState('');
  
  // Form state
  const [fundName, setFundName] = useState('');
  const [fundSymbol, setFundSymbol] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [navValue, setNavValue] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState('');
  const [notes, setNotes] = useState('');
  
  // User's saved fund names (will be populated from existing investments)
  const [userFundNames, setUserFundNames] = useState([]);

  // Add new state variables for live prices
  const [livePrices, setLivePrices] = useState({});
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const [priceError, setPriceError] = useState(null);
  const [currentTotalValue, setCurrentTotalValue] = useState(0);

  // Add new state variables for symbol editing
  const [openSymbolModal, setOpenSymbolModal] = useState(false);
  const [selectedFundForSymbol, setSelectedFundForSymbol] = useState(null);
  const [newSymbol, setNewSymbol] = useState('');

  // Add new state variables for sorting
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

// Group investments by fund name
  const groupInvestmentsByFund = (investments) => {
    const fundMap = new Map();
    
    investments.forEach(investment => {
      const { id, name, value, units, symbol, nav_value, purchase_date, notes } = investment;
      
      if (fundMap.has(name)) {
        const fund = fundMap.get(name);
        fund.value += value;
        fund.units += parseFloat(units || 0);
        if (!fund.symbol && symbol) {
          fund.symbol = symbol;
        }
        fund.transactions.push(investment);
      } else {
        fundMap.set(name, {
          id,
          name,
          symbol: symbol || null,
          value,
          units: parseFloat(units || 0),
          transactions: [investment]
        });
      }
    });
    
    return Array.from(fundMap.values()).map(fund => ({
        id: fund.id,
        name: fund.name,
        symbol: fund.symbol,
        value: fund.value,
        units: fund.units,
        transactions: fund.transactions
    }));
  };

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch debt mutual fund investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setPriceError(null);
      
      console.log('Fetching debt mutual fund investments for user ID:', user.id);
      
      const { data, error } = await supabase
        .from('debt_mutual_fund_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        console.error('Error fetching debt mutual fund investments:', error);
        throw error;
      }
      
      console.log('Fetched debt mutual fund data:', data);
      
      if (data && data.length > 0) {
        const uniqueFundNames = [...new Set(data.map(item => item.name))];
        setUserFundNames(uniqueFundNames);
        
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          symbol: item.symbol,
          value: item.amount_invested,
          navValue: item.nav_value,
          purchaseDate: item.purchase_date,
          units: item.units,
          notes: item.notes
        }));
        
        const groupedInvestments = groupInvestmentsByFund(formattedData);
        setInvestments(groupedInvestments);
        
        const total = groupedInvestments.reduce((sum, fund) => sum + fund.value, 0);
        setTotalValue(total);

        setCurrentTotalValue(total);

        console.log('Total debt mutual fund invested value:', total);

      } else {
        console.log('No debt mutual fund investments found');
        setInvestments([]);
        setTotalValue(0);
        setCurrentTotalValue(0);
        setUserFundNames([]);
      }
    } catch (error) {
      console.error('Error processing debt mutual fund investments:', error);
      setInvestments([]);
      setTotalValue(0);
      setCurrentTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  // Add fetchLivePrices function
  const fetchLivePrices = useCallback(async () => {
    setPriceError(null);
    setLivePriceLoading(true);
    
    try {
      console.log('Starting fetchLivePrices for debt mutual funds...');

      const fundsWithSymbols = investments.filter(fund => fund.symbol && fund.symbol.trim() !== '');
      const uniqueSymbols = [...new Set(fundsWithSymbols.map(fund => fund.symbol.trim()))];
      
      console.log('Unique fund symbols to fetch:', uniqueSymbols);

      if (uniqueSymbols.length === 0) {
        console.log('No valid symbols found to fetch prices');
        setPriceError('No valid symbols found. Please add symbol codes to your investments.');
        setCurrentTotalValue(totalValue);
        return;
      }

      const newPrices = {};
      let hasValidPrices = false;

      const batchSize = 2;
      for (let i = 0; i < uniqueSymbols.length; i += batchSize) {
        const batch = uniqueSymbols.slice(i, i + batchSize);
        console.log(`Processing batch ${i/batchSize + 1}:`, batch);
        
        try {
          if (i > 0) {
            console.log('Adding delay between batches...');
            await new Promise(resolve => setTimeout(resolve, 3000));
          }

          const promises = batch.map(async (symbol) => {
            try {
              const url = getYahooFinanceUrl(symbol);
              console.log(`Fetching price for ${symbol} using URL:`, url);
              
              const response = await axios.get(url, {
                headers: {
                  'Accept': 'application/json'
                },
                timeout: 15000
              });
              
              let data;
              try {
                data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
              } catch (parseError) {
                console.error(`Error parsing response for ${symbol}:`, parseError);
                return null;
              }

              console.log(`Response for ${symbol}:`, data);

              if (data?.chart?.result?.[0]) {
                const result = data.chart.result[0];
                const quote = result.meta;
                
                if (!quote) {
                  console.log(`No quote data for ${symbol}`);
                  return null;
                }

                const regularMarketPrice = quote.regularMarketPrice;
                const previousClose = quote.chartPreviousClose;
                
                if (regularMarketPrice && previousClose) {
                  const change = regularMarketPrice - previousClose;
                  const changePercent = (change / previousClose) * 100;

                  const fund = fundsWithSymbols.find(f => f.symbol.trim() === symbol);
                  if (!fund) {
                    console.error(`No grouped fund found for symbol ${symbol}`);
                    return null;
                  }

                  return {
                    fundName: fund.name,
                    symbol: symbol,
                    price: regularMarketPrice,
                    change,
                    changePercent,
                    currency: quote.currency || 'INR',
                    lastUpdated: new Date().toISOString()
                  };
                } else {
                  console.log(`Missing price data for ${symbol}:`, { regularMarketPrice, previousClose });
                }
              } else {
                console.log(`Invalid response format for ${symbol}:`, data);
              }
              return null;
            } catch (error) {
              console.error(`Error fetching price for ${symbol}:`, error.message);
              if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
              }
              return null;
            }
          });

          const results = await Promise.all(promises);
          console.log('Batch results:', results);
          
          results.forEach((result) => {
            if (result) {
              newPrices[result.fundName] = {
                symbol: result.symbol,
                price: result.price,
                change: result.change,
                changePercent: result.changePercent,
                currency: result.currency,
                lastUpdated: result.lastUpdated
              };
              hasValidPrices = true;
            } else {
                const failedSymbol = batch[results.indexOf(result)];
                console.log(`Fetching failed for symbol: ${failedSymbol}`);
            }
          });

        } catch (error) {
          console.error(`Error processing batch ${i/batchSize + 1}:`, error);
        }
      }

      if (hasValidPrices) {
        console.log('Successfully fetched prices:', newPrices);
        setLivePrices(newPrices);
        setLastPriceUpdate(new Date());

        const newTotalValue = investments.reduce((sum, fund) => {
          const livePriceData = newPrices[fund.name];
          if (livePriceData && livePriceData.price) {
             return sum + (fund.units * livePriceData.price);
          }
          return sum + fund.value; 
        }, 0);

        setCurrentTotalValue(newTotalValue);
        console.log('Calculated current total value:', newTotalValue);

      } else {
        const errorMsg = 'No valid NAVs could be fetched. Please check your symbol codes and try again.';
        console.error(errorMsg);
        setPriceError(errorMsg);
        setCurrentTotalValue(totalValue);
      }

    } catch (error) {
      console.error('Error in fetchLivePrices:', error);
      setPriceError('Failed to fetch NAVs. Please try again later.');
      setCurrentTotalValue(totalValue);
    } finally {
      setLivePriceLoading(false);
    }
  }, [investments, totalValue]);

  // Add handleRefreshPrices function
  const handleRefreshPrices = async () => {
    if (livePriceLoading) return;
    
    setLivePrices({});
    setPriceError(null);
    setLastPriceUpdate(null);
    setLivePriceLoading(true);
    
    try {
      await fetchLivePrices();
    } catch (error) {
      console.error('Error refreshing prices:', error);
      setPriceError('Failed to refresh NAVs. Please try again.');
    } finally {
      setLivePriceLoading(false);
    }
  };

  // Add PriceLoadingIndicator component
  const PriceLoadingIndicator = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="caption" color="text.secondary">
        Fetching NAVs...
      </Typography>
    </Box>
  );

  // Calculate units when NAV and amount invested changes
  useEffect(() => {
    if (navValue && amountInvested && parseFloat(navValue) > 0 && parseFloat(amountInvested) > 0) {
      setUnits((parseFloat(amountInvested) / parseFloat(navValue)).toFixed(4));
    } else {
      setUnits('');
    }
  }, [navValue, amountInvested]);

  // Calculate units when NAV and amount invested changes for edit modal
  useEffect(() => {
    if (editNavValue && editAmountInvested && parseFloat(editNavValue) > 0 && parseFloat(editAmountInvested) > 0) {
      setEditUnits((parseFloat(editAmountInvested) / parseFloat(editNavValue)).toFixed(4));
    } else {
      setEditUnits('');
    }
  }, [editNavValue, editAmountInvested]);
  
  // Load data on component mount and fetch live prices initially
  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  // Pie chart data calculation (based on current value if available, else invested value)
  const pieChartData = useMemo(() => {
    if (investments.length === 0) return { currentData: [], investedData: [] };

    const currentData = investments.map((fund) => {
      const livePriceData = livePrices[fund.name];
      const currentValue = livePriceData && livePriceData.price 
        ? fund.units * livePriceData.price 
        : fund.value;

      return {
        id: fund.id,
        value: currentValue,
        label: fund.name,
      };
    }).sort((a, b) => b.value - a.value);

    const investedData = investments.map((fund) => ({
      id: fund.id,
      value: fund.value,
      label: fund.name,
    })).sort((a, b) => b.value - a.value);

    const currentTotal = currentData.reduce((sum, item) => sum + item.value, 0);
    const investedTotal = investedData.reduce((sum, item) => sum + item.value, 0);

    const currentDataWithPercentage = currentData.map(item => ({
        ...item,
        percentage: currentTotal > 0 ? ((item.value / currentTotal) * 100).toFixed(1) : '0.0'
    }));

     const investedDataWithPercentage = investedData.map(item => ({
        ...item,
        percentage: investedTotal > 0 ? ((item.value / investedTotal) * 100).toFixed(1) : '0.0'
    }));

    return { currentData: currentDataWithPercentage, investedData: investedDataWithPercentage };
  }, [investments, livePrices]);

  // Modal handlers
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  
  const handleReset = () => {
    setFundName('');
    setFundSymbol('');
    setPurchaseDate(new Date());
    setNavValue('');
    setUnits('');
    setAmountInvested('');
    setNotes('');
  };
  
  const handleFundNameChange = (event, newValue) => {
    setFundName(newValue);
    
    if (newValue && typeof newValue === 'string') {
      const selectedExistingFund = investments.find(fund => fund.name === newValue);
      if (selectedExistingFund && selectedExistingFund.symbol) {
        setFundSymbol(selectedExistingFund.symbol);
      } else {
        setFundSymbol('');
      }
    } else {
      setFundSymbol('');
    }
  };

  // Transaction history modal handlers
  const handleOpenTransactionHistory = (fund) => {
    setSelectedFund(fund);
    setFundTransactions(fund.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedFund(null);
    setFundTransactions([]);
  };
  
  // Transaction action handlers
  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Edit transaction handlers
  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    setEditNavValue(currentTransaction.navValue ? currentTransaction.navValue.toString() : '');
    setEditAmountInvested(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    setEditUnits(currentTransaction.units ? currentTransaction.units.toString() : '');
    setEditNotes(currentTransaction.notes || '');
    
    setOpenEditModal(true);
  };
  
  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };
  
  // Delete transaction handlers
  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  // Add new handlers for symbol editing
  const handleEditSymbolClick = () => {
    handleTransactionMenuClose();
    if (!selectedFund) {
       console.error('No fund selected for symbol editing');
       return;
    }
    
    setSelectedFundForSymbol(selectedFund);
    setNewSymbol(selectedFund.symbol || '');
    setOpenSymbolModal(true);
  };

  const handleSymbolModalClose = () => {
    setOpenSymbolModal(false);
    setSelectedFundForSymbol(null);
    setNewSymbol('');
  };

  // Update symbol for all transactions of a fund name
  const handleUpdateSymbol = async () => {
    if (!selectedFundForSymbol || !selectedFundForSymbol.name) {
      console.error('No fund selected or fund name missing for symbol update');
      alert('Cannot update symbol: Fund name missing.');
      return;
    }

    const fundNameToUpdate = selectedFundForSymbol.name;
    const symbolToUpdate = newSymbol.trim().toUpperCase();

    try {
      setLoading(true);
      
      console.log(`Attempting to update symbol for fund: ${fundNameToUpdate} to ${symbolToUpdate}`);

      const { error, count } = await supabase
        .from('debt_mutual_fund_investments')
        .update({ symbol: symbolToUpdate })
        .eq('name', fundNameToUpdate)
        .select();

      if (error) {
        console.error('Error updating symbol:', error);
        throw error;
      } 
      
      console.log(`Successfully updated ${count || 0} records for fund ${fundNameToUpdate}`);

      await fetchInvestments();
      
      handleSymbolModalClose();
      
      alert(`Symbol updated successfully for all '${fundNameToUpdate}' investments.`);
      
    } catch (error) {
      console.error('Error updating symbol:', error);
      alert(`Failed to update symbol: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!fundName || !purchaseDate || !navValue || !amountInvested || !fundSymbol) {
      alert('Please fill all required fields including the symbol code');
      return;
    }

    try {
      setLoading(true);
      
      let fundNameValue = '';
      if (typeof fundName === 'string') {
        fundNameValue = fundName;
      } else if (fundName && typeof fundName === 'object') {
        fundNameValue = fundName.label || '';
      }
      
      if (!fundNameValue) {
        alert('Please enter a valid fund name');
        setLoading(false);
        return;
      }
      
      const newInvestment = {
        name: fundNameValue,
        symbol: fundSymbol.trim().toUpperCase(),
        purchase_date: purchaseDate.toISOString().split('T')[0],
        nav_value: parseFloat(navValue),
        amount_invested: parseFloat(amountInvested),
        units: parseFloat(units),
        notes: notes || null,
        user_id: user.id
      };

      console.log('Creating new debt mutual fund investment:', newInvestment);

      const { data, error } = await supabase
        .from('debt_mutual_fund_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      if (data && data.length > 0) {
        await fetchInvestments();
      }

      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransaction = async () => {
    if (!editNavValue || !editAmountInvested || !editPurchaseDate) {
      alert('Please fill all required fields in the edit form');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      const updatedTransaction = {
        name: currentTransaction.name,
        symbol: currentTransaction.symbol,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0]
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        nav_value: parseFloat(editNavValue),
        amount_invested: parseFloat(editAmountInvested),
        units: parseFloat(editUnits),
        notes: editNotes || null
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      const { data, error } = await supabase
        .from('debt_mutual_fund_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      await fetchInvestments();
      
      if (selectedFund && openHistoryModal) {
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('debt_mutual_fund_investments')
          .select('*')
          .eq('name', selectedFund.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              symbol: item.symbol,
              value: item.amount_invested,
              navValue: item.nav_value,
              purchaseDate: item.purchase_date,
              units: item.units,
              notes: item.notes
            }));
            
            setFundTransactions(formattedTransactions);
          } else {
            handleCloseHistoryModal();
          }
        }
      }

      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      if (!currentTransaction || !currentTransaction.id) {
        console.error('Missing transaction ID for delete');
        alert('Cannot delete transaction: Missing ID');
        return;
      }

      console.log('Deleting transaction:', currentTransaction.id);
      
      const { error } = await supabase
        .from('debt_mutual_fund_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      console.log(`Successfully deleted transaction ${currentTransaction.id}`);

      await fetchInvestments();
      
      if (selectedFund && openHistoryModal) {
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('debt_mutual_fund_investments')
          .select('*')
          .eq('name', selectedFund.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              symbol: item.symbol,
              value: item.amount_invested,
              navValue: item.nav_value,
              purchaseDate: item.purchase_date,
              units: item.units,
              notes: item.notes
            }));
            
            setFundTransactions(formattedTransactions);
          } else {
            handleCloseHistoryModal();
          }
        }
      }

      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Add handleSort function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedInvestments = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = (a.value / totalValue) * 100;
        const bAllocation = (b.value / totalValue) * 100;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      } else if (key === 'currentValue') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        return direction === 'asc' 
          ? aCurrentValue - bCurrentValue
          : bCurrentValue - aCurrentValue;
      } else if (key === 'currentAllocation') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        const aCurrentAllocation = (aCurrentValue / currentTotalValue) * 100;
        const bCurrentAllocation = (bCurrentValue / currentTotalValue) * 100;
        return direction === 'asc' 
          ? aCurrentAllocation - bCurrentAllocation
          : bCurrentAllocation - aCurrentAllocation;
      } else if (key === 'pl') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        const aPL = aCurrentValue - a.value;
        const bPL = bCurrentValue - b.value;
        return direction === 'asc' 
          ? aPL - bPL
          : bPL - aPL;
      }
      return 0;
    });

    setInvestments(sortedInvestments);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/investments/debt')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-tour="inv-prod-summary">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Debt Mutual Funds
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Invested: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(totalValue)}
              </Typography>
              {livePriceLoading ? (
                <PriceLoadingIndicator />
              ) : priceError ? (
                <Chip
                  icon={<ErrorIcon />}
                  label={priceError}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                />
              ) : Object.keys(livePrices).length > 0 ? (
                <>
                  <Typography variant="subtitle1" color="text.secondary">
                    | Current Value: {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      maximumFractionDigits: 0,
                    }).format(currentTotalValue)}
                  </Typography>
                  {lastPriceUpdate && (
                    <Tooltip title={`Last updated: ${lastPriceUpdate.toLocaleTimeString()}`}>
                      <Chip
                        icon={<AccessTimeIcon />}
                        label="Live"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </>
              ) : (
                <Chip
                  icon={<ErrorIcon />}
                  label="No live NAVs available"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Button
            data-tour="inv-prod-live"
            variant="outlined"
            startIcon={livePriceLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefreshPrices}
            disabled={livePriceLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 140,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {livePriceLoading ? 'Updating...' : 'Get Live NAV'}
          </Button>
          
          <Button
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={
              {
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Debt Mutual Fund Investment
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Autocomplete
                value={fundName}
                onChange={handleFundNameChange}
                freeSolo
                autoHighlight
                options={userFundNames}
                getOptionLabel={(option) => {
                  return typeof option === 'string' ? option : option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option => 
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }
                  
                  return filtered;
                }}
                groupBy={(option) => {
                  if (!userFundNames.includes(option)) {
                    return "Custom Entry";
                  }
                  return "Your Fund Names";
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Name of Fund" 
                    required 
                    fullWidth
                    placeholder="Enter or select fund name"
                    helperText="You can type a new fund name or select from your existing funds"
                  />
                )}
              />
              
              <TextField
                label="Symbol Code"
                value={fundSymbol}
                onChange={(e) => setFundSymbol(e.target.value.toUpperCase())}
                required
                fullWidth
                placeholder="Enter fund symbol (e.g., 0P0000ABCD)"
                helperText="Enter the fund's symbol code for live NAV updates"
                inputProps={{
                  style: { textTransform: 'uppercase' }
                }}
              />

              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="NAV"
                value={navValue}
                onChange={(e) => setNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Enter the NAV (Net Asset Value) at purchase"
                sx={numberInputStyle}
              />
              
              <TextField
                label="Amount Invested"
                value={amountInvested}
                onChange={(e) => setAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "1", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={units}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount / NAV)"
              />
              
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Additional details about this investment (optional)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit Transaction
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>

      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Fund: {currentTransaction?.name}
              </Typography>
              <TextField
                label="Symbol Code"
                value={currentTransaction?.symbol || ''}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                helperText="Symbol is edited separately via Transaction History"
              />
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="NAV Value"
                value={editNavValue}
                onChange={(e) => setEditNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Amount Invested"
                value={editAmountInvested}
                onChange={(e) => setEditAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={editUnits}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount / NAV)"
              />
              
              <TextField
                label="Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Additional details about this investment (optional)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      <Modal
        open={openSymbolModal}
        onClose={handleSymbolModalClose}
        aria-labelledby="edit-symbol-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Fund Symbol
          </Typography>
          
          <Stack spacing={3}>
            <Typography variant="subtitle1">
              Fund: {selectedFundForSymbol?.name}
            </Typography>
            
            <TextField
              label="Symbol Code"
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
              fullWidth
              placeholder="Enter fund symbol (e.g., 0P0000ABCD)"
              helperText="Enter the fund's symbol code for live NAV updates"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleSymbolModalClose}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleUpdateSymbol}
                disabled={loading}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {loading ? 'Updating...' : 'Update Symbol'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Fund: {currentTransaction.name}<br />
                Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentTransaction.value)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">No Debt Mutual Funds Found</Typography>
          <Typography variant="body2">
            No debt mutual fund investments found. Start investing to see them here.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Fund Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Fund Name
                        {sortConfig.key === 'name' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('value')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Invested Value
                        {sortConfig.key === 'value' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('currentValue')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Current Value
                        {sortConfig.key === 'currentValue' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('pl')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        P/L
                        {sortConfig.key === 'pl' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Invested %
                        {sortConfig.key === 'allocation' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('currentAllocation')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Current %
                        {sortConfig.key === 'currentAllocation' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((fund, index) => {
                      const livePriceData = livePrices[fund.name];
                      const currentValue = livePriceData && livePriceData.price 
                        ? fund.units * livePriceData.price 
                        : fund.value;

                      const investedPercentage = totalValue > 0 ? ((fund.value / totalValue) * 100).toFixed(1) : '0.0';
                      const currentPercentage = currentTotalValue > 0 ? ((currentValue / currentTotalValue) * 100).toFixed(1) : '0.0';

                      const profitLoss = currentValue - fund.value;
                      const profitLossPercent = fund.value > 0 ? ((profitLoss / fund.value) * 100).toFixed(1) : '0.0';
                      const isProfit = profitLoss >= 0;
                      
                      return (
                        <TableRow
                          key={fund.id}
                          onClick={() => handleOpenTransactionHistory(fund)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: COLORS[index % COLORS.length],
                                  mr: 2,
                                }}
                              />
                              {fund.name}
                              {livePriceData && (
                                <Chip
                                  icon={livePriceData.changePercent >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                  label={`₹${livePriceData.price.toFixed(2)} (${livePriceData.changePercent.toFixed(2)}%)`}
                                  color={livePriceData.changePercent >= 0 ? 'success' : 'error'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 2 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(fund.value)}
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(currentValue)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: profitLoss >= 0 ? 'success.main' : 'error.main',
                                fontWeight: 500,
                              }}
                            >
                              {isProfit ? '+' : ''}
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                                signDisplay: 'never'
                              }).format(Math.abs(profitLoss))}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: isProfit ? 'success.main' : 'error.main',
                                }}
                              >
                                {isProfit ? '+' : ''}{profitLossPercent}%
                              </Typography>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {investedPercentage}%
                          </TableCell>
                          <TableCell align="right">
                            {currentPercentage}%
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View transactions">
                              <IconButton size="small" sx={{ color: 'primary.main' }}>
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                        '& td': { borderBottom: '2px solid', borderColor: 'divider' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(currentTotalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {(() => {
                          if (Object.keys(livePrices).length === 0) {
                            return (
                              <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                ₹0
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                  }}
                                >
                                  0.0%
                                </Typography>
                              </Typography>
                            );
                          }

                          const totalPL = currentTotalValue - totalValue;
                           const totalPLPercent = totalValue > 0 ? ((totalPL / totalValue) * 100).toFixed(1) : '0.0';
                          const isProfit = totalPL >= 0;
                          
                          return (
                            <Typography
                              sx={{
                                color: isProfit ? 'success.main' : 'error.main',
                                fontWeight: 600,
                              }}
                            >
                              {isProfit ? '+' : ''}
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                                signDisplay: 'never'
                              }).format(Math.abs(totalPL))}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: isProfit ? 'success.main' : 'error.main',
                                }}
                              >
                                {isProfit ? '+' : ''}{totalPLPercent}%
                              </Typography>
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Grid container spacing={2}>
              {/* Current Value Distribution */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: 'var(--fx-card-shadow)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Current Distribution
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on live market prices
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                    }}
                  >
                    {pieChartData.currentData.length > 0 && (
                      <PieChart colors={chartPalette}
                        series={[
                          {
                            data: pieChartData.currentData,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { innerRadius: 30, additionalRadius: -30 },
                          },
                        ]}
                        height={300}
                        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                        // slotProps={{
                        //   legend: {
                        //     direction: 'row',
                        //     position: { vertical: 'bottom', horizontal: 'middle' },
                        //     padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        //     itemMarkWidth: 8,
                        //     itemMarkHeight: 8,
                        //     markGap: 5,
                        //     itemGap: 15,
                        //     sx: {
                        //       display: 'flex',
                        //       flexWrap: 'wrap',
                        //       justifyContent: 'center',
                        //       gap: 2,
                        //     },
                        //   },
                        // }}
                        slotProps={{ legend: { hidden: true } }}
                        tooltip={{
                          trigger: 'item',
                          formatter: (params) => {
                            const value = new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(params.value);
                            return `${params.name}: ${value} (${params.percentage}%)`;
                          },
                        }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Invested Value Distribution */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: 'var(--fx-card-shadow)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Invested Distribution
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on purchase values
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                    }}
                  >
                    {pieChartData.investedData.length > 0 && (
                      <PieChart colors={chartPalette}
                        series={[
                          {
                            data: pieChartData.investedData,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { innerRadius: 30, additionalRadius: -30 },
                          },
                        ]}
                        height={300}
                        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                        // slotProps={{
                        //   legend: {
                        //     direction: 'row',
                        //     position: { vertical: 'bottom', horizontal: 'middle' },
                        //     padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        //     itemMarkWidth: 8,
                        //     itemMarkHeight: 8,
                        //     markGap: 5,
                        //     itemGap: 15,
                        //     sx: {
                        //       display: 'flex',
                        //       flexWrap: 'wrap',
                        //       justifyContent: 'center',
                        //       gap: 2,
                        //     },
                        //   },
                        // }}
                        slotProps={{ legend: { hidden: true } }}
                        tooltip={{
                          trigger: 'item',
                          formatter: (params) => {
                            const value = new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(params.value);
                            return `${params.name}: ${value} (${params.percentage}%)`;
                          },
                        }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      
      <Modal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 700 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {selectedFund?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Symbol: {selectedFund?.symbol || 'Not set'} |
                Total Value: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(selectedFund?.value || 0)} |
                Total Units: {(selectedFund?.units || 0).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedFundForSymbol(selectedFund);
                  setNewSymbol(selectedFund?.symbol || '');
                  setOpenSymbolModal(true);
                }}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Edit Symbol
              </Button>
              <IconButton onClick={handleCloseHistoryModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>NAV (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Units</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Notes</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundTransactions.length > 0 ? (
                  fundTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.purchaseDate 
                          ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.navValue 
                          ? parseFloat(transaction.navValue).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(transaction.value)}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.units 
                          ? parseFloat(transaction.units).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {transaction.notes || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {fundTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(fundTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fundTransactions
                        .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0)
                        .toFixed(2)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleCloseHistoryModal}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <GuidedTour steps={TOURS['inv-debt-mutual-fund']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default DebtMutualFund;